import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
    ReactiveFormsModule,
    FormBuilder,
    FormGroup,
    Validators,
} from '@angular/forms';
import { MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { ToastrService } from 'ngx-toastr';
import { SharedService } from '../../services/shared.service';

@Component({
    selector: 'app-change-password-dialog',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatDialogModule,
        MatFormFieldModule,
        MatInputModule,
        MatButtonModule,
        MatIconModule,
    ],
    templateUrl: './change-password-dialog.component.html',
    styleUrl: './change-password-dialog.component.scss',
})
export class ChangePasswordDialogComponent implements OnInit {
    pwForm: FormGroup;
    saving = false;

    showOld     = false;
    showNew     = false;
    showConfirm = false;

    strengthScore = 0;   // 0-4
    strengthLabel = '';
    strengthClass = '';

    constructor(
        private fb: FormBuilder,
        private dialogRef: MatDialogRef<ChangePasswordDialogComponent>,
        private sharedService: SharedService,
        private toastr: ToastrService
    ) {}

    ngOnInit(): void {
        this.pwForm = this.fb.group({
            old_password:     ['', Validators.required],
            new_password:     ['', [Validators.required, Validators.minLength(8)]],
            confirm_password: ['', Validators.required],
        });

        // Mirror mismatch onto the confirm_password control so mat-error can see it.
        // Run on every change to either password field.
        this.pwForm.get('new_password')!.valueChanges.subscribe(() => this.syncMismatch());
        this.pwForm.get('confirm_password')!.valueChanges.subscribe(() => this.syncMismatch());
    }

    private syncMismatch(): void {
        const confirmCtrl = this.pwForm.get('confirm_password')!;
        const newVal      = this.pwForm.get('new_password')!.value;
        const confirmVal  = confirmCtrl.value;

        if (newVal && confirmVal && newVal !== confirmVal) {
            // Merge mismatch into the control's existing errors so required etc. still work.
            confirmCtrl.setErrors({ ...confirmCtrl.errors, mismatch: true });
        } else {
            // Remove only the mismatch key; leave any other errors intact.
            if (confirmCtrl.hasError('mismatch')) {
                const { mismatch, ...rest } = confirmCtrl.errors ?? {};
                confirmCtrl.setErrors(Object.keys(rest).length ? rest : null);
            }
        }
    }

    evaluateStrength(event: Event): void {
        const value = (event.target as HTMLInputElement).value;
        let score = 0;
        if (value.length >= 8)           score++;
        if (/[A-Z]/.test(value))         score++;
        if (/[0-9]/.test(value))         score++;
        if (/[^A-Za-z0-9]/.test(value))  score++;

        this.strengthScore = score;

        const map: Record<number, { label: string; cls: string }> = {
            1: { label: 'Weak',   cls: 'strength-weak'   },
            2: { label: 'Fair',   cls: 'strength-fair'   },
            3: { label: 'Good',   cls: 'strength-good'   },
            4: { label: 'Strong', cls: 'strength-strong' },
        };

        this.strengthLabel = map[score]?.label ?? '';
        this.strengthClass = map[score]?.cls   ?? '';
    }

    submit(): void {
        if (this.pwForm.invalid) {
            this.pwForm.markAllAsTouched();
            return;
        }

        this.saving = true;

        const payload = {
            current_password: this.pwForm.value.old_password,
            new_password: this.pwForm.value.new_password,
        };

        this.sharedService.putRequest('/user/password', payload).subscribe(
            () => {
                this.saving = false;
                this.dialogRef.close(true);
            },
            (err) => {
                this.saving = false;
                this.toastr.error('Error: ' + (err?.error?.message?.message ?? 'Something went wrong'));
            }
        );
    }
}